import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { techniciansApi } from "../services/api";
import { useNotification } from "../context/NotificationContext";

// New technicians start WITHOUT admin: full access is an explicit choice (the backend default is is_admin=false too).
const EMPTY_FORM = { first_name: "", last_name: "", email: "", phone: "", is_admin: false };

function AdminToggle({ value, onChange }) {
  return (
    <label className="flex items-center gap-2 cursor-pointer select-none" dir="rtl">
      <span className="text-sm text-gray-700">מנהל (גישה מלאה למערכת)</span>
      {/* A bare <div onClick> here was invisible to keyboard/screen-reader users -- not in the
          Tab order, no role/aria-checked, no way to activate it with Enter/Space -- so neither
          granting nor revoking admin access through this control was reachable without a mouse.
          A real <button role="switch"> gets native Tab/Enter/Space handling for free. */}
      <button
        type="button"
        onClick={onChange}
        role="switch"
        aria-checked={value}
        aria-label="מנהל (גישה מלאה למערכת)"
        style={{
          position: "relative",
          width: 36,
          height: 20,
          borderRadius: 10,
          flexShrink: 0,
          border: "none",
          padding: 0,
          backgroundColor: value ? "#2563eb" : "#9ca3af",
          boxShadow: value ? "0 0 0 3px rgba(37,99,235,0.25)" : "none",
          transition: "background-color 0.2s ease, box-shadow 0.2s ease",
          cursor: "pointer",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: 2,
            width: 16,
            height: 16,
            borderRadius: "50%",
            backgroundColor: "white",
            boxShadow: "0 1px 3px rgba(0,0,0,0.35)",
            transform: value ? "translateX(16px)" : "translateX(2px)",
            transition: "transform 0.2s ease",
          }}
        />
      </button>
    </label>
  );
}

export default function TechniciansPage() {

  const qc = useQueryClient();
  const { addNotification } = useNotification();
  const [showForm, setShowForm] = useState(false);
  const [form, setForm]         = useState(EMPTY_FORM);
  const [editing, setEditing]   = useState(null);

  const { data: technicians = [], isLoading, isError: listFailed, refetch: refetchList } = useQuery({
    queryKey: ["technicians"],
    queryFn: () => techniciansApi.list(),
  });

  const createMutation = useMutation({
    mutationFn: techniciansApi.create,
    onSuccess: () => {
      addNotification("טכנאי נוצר בהצלחה", "success");
      qc.invalidateQueries({ queryKey: ["technicians"] });
      setShowForm(false);
      setForm(EMPTY_FORM);
    },
    onError: (err) => addNotification(err?.response?.data?.detail || "שגיאה ביצירת הטכנאי", "error"),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => techniciansApi.update(id, data),
    onSuccess: (_data, variables) => {
      addNotification("פרטי הטכנאי עודכנו", "success");
      qc.invalidateQueries({ queryKey: ["technicians"] });
      // Only close the edit form if it still belongs to the technician just saved -- the user
      // may have switched to editing someone else while this request was in flight (`editing`
      // is a single page-wide slot), and closing unconditionally silently discarded that
      // unrelated, unsaved edit.
      setEditing((current) => (current?.id === variables.id ? null : current));
    },
    onError: (err) => addNotification(err?.response?.data?.detail || "שגיאה בעדכון פרטי הטכנאי", "error"),
  });

  const deleteMutation = useMutation({
    mutationFn: techniciansApi.delete,
    onSuccess: () => {
      addNotification("הטכנאי הוסר בהצלחה", "success");
      qc.invalidateQueries({ queryKey: ["technicians"] });
    },
    onError: (err) => addNotification(err?.response?.data?.detail || "שגיאה בהסרת הטכנאי", "error"),
  });

  const handleDelete = (technician) => {
    if (!window.confirm(`להסיר את ${technician.first_name} ${technician.last_name} מרשימת הטכנאים? הקריאות יישמרו.`)) return;
    deleteMutation.mutate(technician.id);
  };

  const handleCreate = (e) => {
    e.preventDefault();
    createMutation.mutate(form);
  };

  const handleUpdate = (e) => {
    e.preventDefault();
    const { id, ...data } = editing;
    updateMutation.mutate({ id, data });
  };

  const field = (key, label, type = "text", placeholder = "") => (
    <div>
      <label className="block text-xs text-gray-500 mb-1">{label}</label>
      <input
        type={type}
        required={["first_name", "last_name", "email"].includes(key)}
        placeholder={placeholder}
        value={form[key]}
        onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
        className="w-full border rounded-lg px-3 py-2 text-sm"
      />
    </div>
  );

  return (
    <div className="space-y-5 max-w-4xl mx-auto" dir="rtl">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-800">ניהול טכנאים</h1>
        <button
          onClick={() => { setShowForm(!showForm); setEditing(null); }}
          className="bg-brand-600 hover:bg-brand-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
        >
          + טכנאי חדש
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleCreate} className="bg-white rounded-xl shadow-sm border p-6 space-y-4">
          <h2 className="font-semibold text-gray-700">הוספת טכנאי</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {field("first_name", "שם פרטי",   "text",  "ישראל")}
            {field("last_name",  "שם משפחה",  "text",  "ישראלי")}
            {field("email",      "כתובת מייל", "email", "tech@prodigy.co.il")}
            {field("phone",      "טלפון נייד", "tel",   "050-0000000")}
          </div>
          <div className="pt-1">
            <p className="text-xs text-gray-500 mb-2">הרשאות</p>
            <AdminToggle
              value={form.is_admin}
              onChange={() => setForm((f) => ({ ...f, is_admin: !f.is_admin }))}
            />
          </div>
          {createMutation.isError && (
            <p className="text-red-600 text-sm">{createMutation.error?.response?.data?.detail}</p>
          )}
          <div className="flex gap-2">
            <button type="submit" disabled={createMutation.isPending}
              className="bg-brand-600 text-white px-5 py-2 rounded-lg text-sm font-medium disabled:opacity-50 transition-colors">
              {createMutation.isPending ? "שומר..." : "הוסף טכנאי"}
            </button>
            <button type="button" onClick={() => { setShowForm(false); setForm(EMPTY_FORM); }}
              className="border px-5 py-2 rounded-lg text-sm hover:bg-gray-50">
              ביטול
            </button>
          </div>
        </form>
      )}

      {isLoading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600" />
        </div>
      ) : listFailed && technicians.length === 0 ? (
        // a failed request is not "no technicians": an admin who reads it as empty would start re-creating them
        <div role="alert" className="text-center py-16 text-red-600">
          <p className="font-medium">לא ניתן לטעון את רשימת הטכנאים</p>
          <button onClick={() => refetchList()} className="mt-2 text-sm text-brand-600 hover:underline">נסה שוב</button>
        </div>
      ) : technicians.length === 0 ? (
        <div className="text-center py-16 text-gray-400">לא קיימים טכנאים במערכת</div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b text-gray-500 text-xs uppercase">
              <tr>
                <th className="text-right px-4 py-3">שם</th>
                <th className="text-right px-4 py-3">מייל</th>
                <th className="text-right px-4 py-3">טלפון</th>
                <th className="text-right px-4 py-3">הרשאה</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {technicians.map((t) => (
                <tr key={t.id} className="border-b last:border-0 hover:bg-gray-50">
                  {editing?.id === t.id ? (
                    <>
                      <td className="px-4 py-2">
                        <div className="flex gap-1">
                          <input value={editing.first_name}
                            onChange={(e) => setEditing((ed) => ({ ...ed, first_name: e.target.value }))}
                            className="border rounded px-2 py-1 text-sm w-24" />
                          <input value={editing.last_name}
                            onChange={(e) => setEditing((ed) => ({ ...ed, last_name: e.target.value }))}
                            className="border rounded px-2 py-1 text-sm w-28" />
                        </div>
                      </td>
                      <td className="px-4 py-2">
                        <input value={editing.email} type="email"
                          onChange={(e) => setEditing((ed) => ({ ...ed, email: e.target.value }))}
                          className="border rounded px-2 py-1 text-sm w-full" />
                      </td>
                      <td className="px-4 py-2">
                        <input value={editing.phone || ""}
                          onChange={(e) => setEditing((ed) => ({ ...ed, phone: e.target.value }))}
                          className="border rounded px-2 py-1 text-sm w-32" />
                      </td>
                      <td className="px-4 py-2">
                        <AdminToggle
                          value={editing.is_admin}
                          onChange={() => setEditing((ed) => ({ ...ed, is_admin: !ed.is_admin }))}
                        />
                      </td>
                      <td className="px-4 py-2">
                        <div className="flex gap-2 justify-end">
                          <button onClick={handleUpdate} disabled={updateMutation.isPending}
                            className="text-xs bg-brand-600 text-white px-3 py-1 rounded disabled:opacity-50">
                            שמור
                          </button>
                          <button onClick={() => setEditing(null)}
                            className="text-xs border px-3 py-1 rounded hover:bg-gray-50">
                            ביטול
                          </button>
                        </div>
                      </td>
                    </>
                  ) : (
                    <>
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-800">
                          {t.first_name} {t.last_name}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-600">{t.email}</td>
                      <td className="px-4 py-3 text-gray-500">{t.phone || "—"}</td>
                      <td className="px-4 py-3">
                        {t.is_admin ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                            מנהל
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                            טכנאי
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                        <button
                          onClick={() =>
                            setEditing({
                              id: t.id,
                              first_name: t.first_name,
                              last_name: t.last_name,
                              email: t.email,
                              phone: t.phone || "",
                              is_admin: t.is_admin,
                            })
                          }
                          className="text-xs text-brand-600 hover:underline"
                        >
                          עריכה
                        </button>
                        <button type="button" onClick={() => handleDelete(t)}
                          disabled={deleteMutation.isPending}
                          className="text-xs text-red-600 hover:underline disabled:opacity-50"
                          aria-label={`הסר טכנאי ${t.first_name} ${t.last_name}`}>
                          הסרה
                        </button>
                        </div>
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
